import * as z from 'zod';

import { PaymentAddress } from '../Address';
import { CompanyId, PayeeId } from '../BrandedIds';
import { OptionalPhoneNumber } from '../Phone';
import { DueMonthDay, zodDateOrString } from '../utils/Zod';

export const PayeeType = z.enum(['TAX', 'INSURANCE', 'MORTGAGE_INSURANCE', 'OTHER']);
export type PayeeType = z.infer<typeof PayeeType>;

export const PayeeTaxType = z.enum(['COUNTY', 'CITY', 'SCHOOL', 'VILLAGE', 'TOWN', 'OTHER']);
export type PayeeTaxType = z.infer<typeof PayeeTaxType>;

export const PAYEE_TAX_OPTION_VALUES = [1, 2, 3, 4] as const;
export const PayeeTaxOption = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
export type PayeeTaxOption = z.infer<typeof PayeeTaxOption>;
export const DEFAULT_PAYEE_TAX_OPTION: PayeeTaxOption = 2;

export const PayeePaymentMethod = z.enum(['CHECK', 'WIRE', 'ACH', 'AUTHORIZED_ACH']);
export type PayeePaymentMethod = z.infer<typeof PayeePaymentMethod>;

export const Payee = z.object({
  id: PayeeId,
  companyId: CompanyId,
  payeeType: PayeeType,
  taxType: PayeeTaxType.optional(),
  taxOption: PayeeTaxOption.optional(),
  externalPayeeId: z.string().min(1),
  name: z.string().min(1),
  address: PaymentAddress.optional(),
  phone: OptionalPhoneNumber,
  dueDates: z.array(DueMonthDay).max(4).default([]),
  paymentMethod: PayeePaymentMethod,
  isActive: z.boolean(),
  deletedAt: zodDateOrString.optional(),
  createdAt: zodDateOrString,
  updatedAt: zodDateOrString,
});
export type Payee = z.infer<typeof Payee>;

export const LegacyEscrowPayeeSnapshot = z.object({
  name: z.string().min(1),
  address: PaymentAddress.optional(),
});
export type LegacyEscrowPayeeSnapshot = z.infer<typeof LegacyEscrowPayeeSnapshot>;

// Read-path discriminated union mirroring the GraphQL `Payee` interface
// (implemented by `DirectoryPayee` and `LegacyEscrowPayeeSnapshot`).
// `__typename` is the discriminator — resolvers stamp it so Apollo / the
// interface's `__resolveType` can route to the right implementer. Provenance
// is encoded at the type level so the `LegacyEscrowPayeeSnapshot` arm cannot
// leak directory-only fields.
export type ResolvedPayee =
  | (Payee & { __typename: 'DirectoryPayee' })
  | (LegacyEscrowPayeeSnapshot & { __typename: 'LegacyEscrowPayeeSnapshot' });

export const PayeeInput = Payee.omit({
  id: true,
  companyId: true,
  deletedAt: true,
  createdAt: true,
  updatedAt: true,
}).superRefine((data, ctx) => {
  if (data.payeeType === 'TAX') {
    if (!data.taxType) {
      ctx.addIssue({ code: 'custom', path: ['taxType'], message: 'Required for tax payees' });
    }
    if (data.taxOption === undefined) {
      ctx.addIssue({ code: 'custom', path: ['taxOption'], message: 'Required for tax payees' });
    }
    if (data.dueDates.length < 1) {
      ctx.addIssue({ code: 'custom', path: ['dueDates'], message: 'At least one due date is required' });
    }
  } else {
    if (data.taxType !== undefined) {
      ctx.addIssue({ code: 'custom', path: ['taxType'], message: 'Only allowed for tax payees' });
    }
    if (data.taxOption !== undefined) {
      ctx.addIssue({ code: 'custom', path: ['taxOption'], message: 'Only allowed for tax payees' });
    }
    if (data.dueDates.length > 0) {
      ctx.addIssue({ code: 'custom', path: ['dueDates'], message: 'Only allowed for tax payees' });
    }
  }
});
export type PayeeInput = z.infer<typeof PayeeInput>;

export const ListPayeesInput = z.object({
  take: z.number().int().positive(),
  skip: z.number().int().nonnegative(),
  query: z.string().optional(),
  payeeTypes: z.array(PayeeType).optional(),
  includeInactive: z.boolean().optional(),
});
export type ListPayeesInput = z.infer<typeof ListPayeesInput>;
